import React, { useMemo } from "react";
import clsx from "clsx";
import isEmpty from "lodash/isEmpty";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import ReactHTMLPicture from "@micado-digital/react-htmlpicture/ReactHTMLPicture";
import ReactLink from "@micado-digital/react-link/ReactLink";
import ReactSVGIcon from "@micado-digital/react-svg-icon/ReactSVGIcon";
import ToggleBookmark from "@micado-digital/react-bookmark/ReactBookmark/toggleBookmark";
import FallbackImage from "@micado-digital/react-ui/components/FallbackImage";
import Price from "@micado-digital/react-shop/components/Price";
import useLocals from "@micado-digital/react-ui/hooks/useLocals";
import Local from "@micado-digital/react-ui/components/Local";
import localDE from "@micado-digital/react-shop/locals/de.json";
import localEN from "@micado-digital/react-shop/locals/en.json";
import localIT from "@micado-digital/react-shop/locals/it.json";
import useVintage from "../../hooks/useVintage";
import Tag from "../Tag";

import styles from "./ProductTeaser.styles";

const ProductTeaser = ({ lang, product }) => {
	const {
		attributes: _attributes = [],
		awardTag,
		combinationID = 0,
		description,
		features = [],
		mediaList,
		pageName,
		price,
		title
	} = product || {};
	const { REACT_APP_PATH } = process.env;
	const { amount, originalAmount, priceOption, scaleUnitAmount, scaleUnitTitle } = price || {};
	const vintage = useVintage({ features });
	const css = styles();
	const l = useLocals({
		lang: lang,
		res: {
			de: localDE,
			en: localEN,
			it: localIT
		}
	});

	const link = useMemo(() => {
		return `${pageName}${combinationID ? `?combinationID=${combinationID}` : ``}`;
	}, [combinationID, pageName]);

	const attributes = useMemo(() => {
		return _attributes?.map(({ title, value }) => {
			return `<span>${title}</span>: ${value}`;
		});
	}, [_attributes]);

	return (
		<div className={clsx(css.productTeaser, "mco-shop-product-teaser")}>
			<div className={clsx(css.image, "mco-shop-product-teaser__image-wrapper")}>
				{!isEmpty(mediaList) ? (
					<ReactHTMLPicture
						media={mediaList?.[0]}
						mediaFormats={{
							xs: "product-teaser"
						}}
					/>
				) : (
					<div className={css.fallbackImage}>
						<FallbackImage />
					</div>
				)}
			</div>
			<div className={clsx(css.content, "mco-shop-product-teaser__content")}>
				<Typography className={clsx(css.title, "mco-shop-product-teaser__title")}>
					{title}
				</Typography>
				{!isEmpty(vintage) && (
					<Typography className={clsx(css.year, "mco-shop-product-teaser__year")}>
						{vintage}
					</Typography>
				)}
				{!isEmpty(attributes) && (
					<Typography
						className={clsx(css.attributes, "mco-shop-product-teaser__attributes")}
						dangerouslySetInnerHTML={{
							__html: `${!isEmpty(attributes) ? `${attributes?.join(", ")}` : ``}`
						}}
						variant="body2"
					/>
				)}
				<Price
					classNames={css.price}
					amount={amount}
					lang={lang}
					originalAmount={originalAmount}
					priceOption={priceOption}
					scaleUnitAmount={scaleUnitAmount}
					scaleUnitTitle={scaleUnitTitle}
				/>
				<div className={clsx(css.buttonWrapper, "mco-shop-product-teaser__button-wrapper")}>
					<ReactLink className={clsx(css.button, "mco-shop-product-teaser__button")} to={link}>
						<Button color="primary" fullWidth variant="contained">
							<Local identifier="reactShop.productOverview.buttons.details" l={l} />
						</Button>
					</ReactLink>
				</div>
			</div>
			<div className={clsx(css.overlay, "mco-shop-product-teaser__overlay")}>
				{description && (
					<Typography
						className={clsx(css.description, "mco-shop-product-teaser__description")}
						dangerouslySetInnerHTML={{ __html: description }}
						variant="body2"
					/>
				)}
			</div>
			{!isEmpty(awardTag) && (
				<div className={clsx(css.tag, "mco-shop-product-teaser__tag")}>
					<Tag tag={awardTag} />
				</div>
			)}
			<div className={clsx(css.bookmark, "mco-shop-product-teaser__bookmark")}>
				<ToggleBookmark
					componentBookmarked={
						<ReactSVGIcon
							color="primary"
							src={`${REACT_APP_PATH}/img/icons/shop/bookmark-selected.svg`}
							size={24}
						/>
					}
					componentBookmarkedHover={
						<ReactSVGIcon
							color="primary"
							src={`${REACT_APP_PATH}/img/icons/shop/bookmark-selected.svg`}
							size={24}
						/>
					}
					componentDefault={
						<ReactSVGIcon
							color="primary"
							src={`${REACT_APP_PATH}/img/icons/shop/bookmark.svg`}
							size={24}
						/>
					}
					componentHover={
						<ReactSVGIcon
							color="primary"
							src={`${REACT_APP_PATH}/img/icons/shop/bookmark-selected.svg`}
							size={24}
						/>
					}
					data={{ ...product }}
					validationFields={["id", "objectType"]}
				/>
			</div>
		</div>
	);
};

export default ProductTeaser;
